import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { Button, Form, Modal, Row, Col, Tabs, Tab, Table, Spinner, Pagination } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { Salvar, Cancelar } from "../Generals/buttons";


const TAB_CONTENT_MIN_HEIGHT = 420;
const ITEMS_PER_PAGE = 10;

const campos = {
  cadastros: [
    { name: "entidades", label: "Entidades" },
    { name: "empresas", label: "Empresas" },
    { name: "fornecedores", label: "Fornecedores" },
    { name: "funcionarios", label: "Funcionários" },
    { name: "tipos_documento", label: "Tipos Documento" },
    { name: "indices_correcao", label: "Índices de Correção" },
    { name: "documentos", label: "Documentos" },
    { name: "tipos_empresa", label: "Tipos de Fornecedor" },
    { name: "tipos_pagamento", label: "Tipos Pagamento" },
  ],
  configuracoes: [
    { name: "usuarios", label: "Usuários" },
    { name: "grupos_usuarios", label: "Grupos de Usuário" },
    { name: "configuracoes_campos", label: "Campos de Configuração" },
    { name: "configuracoes_email", label: "E-mail para Notificação" },
    { name: "tipos_agendamento", label: "Tipos de Agendamento" },
    { name: "gerar_agendamentos", label: "Gerar Agendamentos" },
    { name: "campos_customizados", label: "Campos Customizados" },
    //{ name: "agendamentos", label: "Agendamentos" },
  ],
  parametrosGlobais: [
    { name: "parametros", label: "Parâmetros" },
  ],
};

const GruposUsuarioModal = ({ show, handleClose, grupo, fetchGrupos, entidadeId }) => {
  const [activeTab, setActiveTab] = useState("menu");
  const [descricao, setDescricao] = useState("");
  const [permissoes, setPermissoes] = useState({});
  const [empresasIds, setEmpresasIds] = useState([]);
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);
  const [empresasLoading, setEmpresasLoading] = useState(false);
  const [empresasError, setEmpresasError] = useState(null);

  const [tiposDocumentoIds, setTiposDocumentoIds] = useState([]);
  const [tiposDocumentoDisponiveis, setTiposDocumentoDisponiveis] = useState([]);
  const [tiposDocumentoLoading, setTiposDocumentoLoading] = useState(false);
  const [tiposDocumentoError, setTiposDocumentoError] = useState(null);

  const [pageEmpresas, setPageEmpresas] = useState(1);
  const [pageTiposDoc, setPageTiposDoc] = useState(1);
  const [situacao, setSituacao] = useState("A");
  const [sortTiposDocColumn, setSortTiposDocColumn] = useState("nome");
  const [sortTiposDocOrder, setSortTiposDocOrder] = useState("asc");

  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const token = localStorage.getItem("authToken");


  // --- Carregar empresas da entidade (classificacao = 'V') ---
  const carregarEmpresas = useCallback(async () => {
  if (!entidadeId) return;
  setEmpresasLoading(true);
  setEmpresasError(null);
  try {
    const res = await api.get(
      `${BASE_URL}/api/empresas/ListarEmpresas/${entidadeId}`,
      {
        params: { classificacao: "V" },
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const lista = Array.isArray(res.data?.data) ? res.data.data : [];
    lista.sort((a, b) => {
      const la = (a.fantasia || a.razao_social || "").toLocaleLowerCase();
      const lb = (b.fantasia || b.razao_social || "").toLocaleLowerCase();
      return la.localeCompare(lb);
    });
    setEmpresasDisponiveis(lista);
  } catch (err) {
    console.error("Erro ao carregar empresas da entidade", err);
    setEmpresasError("Erro ao carregar empresas");
  } finally {
    setEmpresasLoading(false);
  }
}, [entidadeId, token]);


  // --- Carregar tipos de documento da entidade ---
  const carregarTiposDocumento = useCallback(async () => {
    if (!entidadeId) return;
    setTiposDocumentoLoading(true);
    setTiposDocumentoError(null);
    try {
      const res = await api.get(
        `${BASE_URL}/api/tiposDocumento/ListarTiposDocumento/${entidadeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const lista = Array.isArray(res.data?.data) ? res.data.data : [];
      lista.sort((a, b) => {
        const na = (a.nome || "").toLocaleLowerCase();
        const nb = (b.nome || "").toLocaleLowerCase();
        return na.localeCompare(nb);
      });
      setTiposDocumentoDisponiveis(lista);
    } catch (err) {
      console.error("Erro ao carregar tipos de documento", err);
      setTiposDocumentoError("Erro ao carregar tipos de documento");
    } finally {
      setTiposDocumentoLoading(false);
    }
}, [entidadeId, token]);

  // Carrega empresas e tipos de documento ao abrir o modal; reseta páginas
  useEffect(() => {
    if (show) {
      carregarEmpresas();
      carregarTiposDocumento();
      setPageEmpresas(1);
      setPageTiposDoc(1);
    }
  }, [show, carregarEmpresas, carregarTiposDocumento]);

  // Popula o modal com o retorno do backend; quando abre sem grupo (novo), limpa o formulário
  useEffect(() => {
    if (!show) return;
    setDescricao(grupo?.descricao || "");
    setSituacao(grupo?.situacao === "I" ? "I" : "A");

    const novasPerms = {};
    [...campos.cadastros, ...campos.configuracoes, ...campos.parametrosGlobais]
      .filter((i) => i && i.name)
      .forEach(({ name }) => {
        const v = grupo?.[name];
        novasPerms[name] = (v === "S" || v === "N") ? v : "N";
      });

    // Campos enviados no save que podem não estar nas abas (ex.: agendamentos, tipos_pagamento) — preservar do backend
    const camposEnviados = [
      "entidades", "empresas", "fornecedores", "funcionarios", "tipos_empresa", "tipos_documento",
      "indices_correcao", "documentos", "usuarios", "grupos_usuarios",
      "configuracoes_campos", "configuracoes_email", "tipos_agendamento",
      "agendamentos", "gerar_agendamentos", "tipos_pagamento", "campos_customizados", "parametros"
    ];
    camposEnviados.forEach((campo) => {
      if (novasPerms[campo] === undefined) {
        const v = grupo?.[campo];
        novasPerms[campo] = (v === "S" || v === "N") ? v : "N";
      }
    });

    setPermissoes(novasPerms);
    setEmpresasIds(Array.isArray(grupo?.empresas_grupo) ? grupo.empresas_grupo.map((e) => e.id) : []);
    setTiposDocumentoIds(Array.isArray(grupo?.tiposDocumento_grupo) ? grupo.tiposDocumento_grupo.map((t) => t.id) : []);
/*
    if (grupo) {
      console.log("[GruposUsuarioModal] JSON retornado do backend (permissões / dados do grupo):", JSON.stringify(grupo, null, 2));
    }
    */
  }, [show, grupo]);

  const togglePermissao = (campo) => {
    setPermissoes((prev) => ({
      ...prev,
      [campo]: prev[campo] === "S" ? "N" : "S",
    }));
  };

  const toggleEmpresa = (idEmpresa) => {
    setEmpresasIds((prev) =>
      prev.includes(idEmpresa)
        ? prev.filter((id) => id !== idEmpresa)
        : [...prev, idEmpresa]
    );
  };

  const toggleTipoDocumento = (idTipoDocumento) => {
    setTiposDocumentoIds((prev) =>
      prev.includes(idTipoDocumento)
        ? prev.filter((id) => id !== idTipoDocumento)
        : [...prev, idTipoDocumento]
    );
  };
/*
  const selecionarTodas = () => {
    setEmpresasIds(empresasDisponiveis.map((e) => e.id));
  };

  const desmarcarTodas = () => {
    setEmpresasIds([]);
  };
*/
  const handleSave = async () => {
    const camposFixos = [
      "entidades", "empresas", "fornecedores", "funcionarios", "tipos_empresa", "tipos_documento",
      "indices_correcao", "documentos", "usuarios", "grupos_usuarios",
      "configuracoes_campos", "configuracoes_email", "tipos_agendamento",
      "agendamentos", "gerar_agendamentos", "tipos_pagamento", "campos_customizados", "parametros"
    ];

    // Campos do menu: sempre enviar apenas "S" ou "N" (nunca array/objeto)
    const payload = {
      descricao,
      situacao,
      ...Object.fromEntries(
        camposFixos.map((campo) => [campo, permissoes[campo] === "S" ? "S" : "N"])
      ),
      empresas_ids: empresasIds,
      tipos_documento_ids: tiposDocumentoIds,
    };

    // Log do body da requisição (para debug)
    //console.log("[GruposUsuarioModal] Salvar - body da requisição:", JSON.stringify(payload, null, 2));

    try {
      if (grupo) {
        await api.put(`${BASE_URL}/api/gruposusuario/EditarGrupoUsuario/${grupo.id}`, payload);
      } else {
        const response = await api.post(`${BASE_URL}/api/gruposusuario/InserirGrupoUsuario/${entidadeID}`, payload);
        fetchGrupos();
        handleOnHide({
          id: response.data?.data?.id,
          descricao,
        });
        return;
      }
      fetchGrupos();
      handleOnHide();
    } catch (err) {
      console.error("Erro ao salvar grupo de usuário", err);
    }
  };

  const handleOnHide = (resultado) => {
    const resultadoValido = resultado && !resultado.preventDefault && !resultado.nativeEvent
      ? resultado
      : undefined;
    setDescricao("");
    setSituacao("A");
    setPermissoes({});
    setEmpresasIds([]);
    setTiposDocumentoIds([]);
    setActiveTab("menu");
    handleClose(resultadoValido);
  };

  const totalSelecionadas = empresasIds.length;
  const totalEmpresas = empresasDisponiveis.length;
  const totalTiposDocSelecionados = tiposDocumentoIds.length;
  const totalTiposDoc = tiposDocumentoDisponiveis.length;

  // Paginação Empresas
  const totalPagesEmpresas = Math.ceil(totalEmpresas / ITEMS_PER_PAGE) || 1;
  const startRecordEmpresas = totalEmpresas === 0 ? 0 : (pageEmpresas - 1) * ITEMS_PER_PAGE + 1;
  const endRecordEmpresas = Math.min(pageEmpresas * ITEMS_PER_PAGE, totalEmpresas);
  const inicioEmpresas = (pageEmpresas - 1) * ITEMS_PER_PAGE;
  const empresasPagina = empresasDisponiveis.slice(inicioEmpresas, inicioEmpresas + ITEMS_PER_PAGE);

  // Ordenação Tipos Documento
  const tiposDocumentoOrdenados = useMemo(() => {
    const col = sortTiposDocColumn || "nome";
    const order = sortTiposDocOrder === "desc" ? -1 : 1;
    return [...tiposDocumentoDisponiveis].sort((a, b) => {
      const va = String(a[col] ?? "").toLocaleLowerCase();
      const vb = String(b[col] ?? "").toLocaleLowerCase();
      return order * va.localeCompare(vb);
    });
  }, [tiposDocumentoDisponiveis, sortTiposDocColumn, sortTiposDocOrder]);

  const handleSortTiposDoc = (column) => {
    if (sortTiposDocColumn === column) {
      setSortTiposDocOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortTiposDocColumn(column);
      setSortTiposDocOrder("asc");
    }
    setPageTiposDoc(1);
  };

  const selecionarTodosTiposDoc = () => {
    setTiposDocumentoIds(tiposDocumentoDisponiveis.map((t) => t.id));
  };
  const desmarcarTodosTiposDoc = () => {
    setTiposDocumentoIds([]);
  };

  // Paginação Tipos Documento (sobre a lista ordenada)
  const totalPagesTiposDoc = Math.ceil(totalTiposDoc / ITEMS_PER_PAGE) || 1;
  const startRecordTiposDoc = totalTiposDoc === 0 ? 0 : (pageTiposDoc - 1) * ITEMS_PER_PAGE + 1;
  const endRecordTiposDoc = Math.min(pageTiposDoc * ITEMS_PER_PAGE, totalTiposDoc);
  const inicioTiposDoc = (pageTiposDoc - 1) * ITEMS_PER_PAGE;
  const tiposDocPagina = tiposDocumentoOrdenados.slice(inicioTiposDoc, inicioTiposDoc + ITEMS_PER_PAGE);

  const handleChangePageEmpresas = (page) => setPageEmpresas(page);
  const handleChangePageTiposDoc = (page) => setPageTiposDoc(page);

  return (
    <Modal
      show={show}
      onHide={handleOnHide}
      size="lg"
      backdrop="static"
      animation={false}
      dialogClassName="modal-dialog-top"
    >
      <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
          {grupo ? "Grupo de Usuário" : "Novo Grupo de Usuário"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
        <Tabs
          id="grupos-usuario-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "menu")}
          className="mb-3"
        >
          <Tab eventKey="menu" title="Menu">
            <div style={{ minHeight: TAB_CONTENT_MIN_HEIGHT }}>
              <Form>
                <Form.Group controlId="descricaoGrupo">
                  <Form.Label>Descrição do Grupo</Form.Label>
                  <Form.Control
                    type="text"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="situacaoGrupo" className="mt-3">
                  <Form.Check
                    type="switch"
                    id="situacao-grupo"
                    label={situacao === "A" ? "Ativo" : "Inativo"}
                    checked={situacao === "A"}
                    onChange={() => setSituacao((s) => (s === "A" ? "I" : "A"))}
                  />
                </Form.Group>

                <hr />
                <h5>Cadastros</h5>
                <Row>
                  {campos.cadastros
                    .filter((i) => i && i.name)
                    .map(({ name, label }) => (
                      <Col md={4} key={name}>
                        <Form.Check
                          type="switch"
                          id={`cad-${name}`}
                          label={label}
                          checked={permissoes[name] === "S"}
                          onChange={() => togglePermissao(name)}
                        />
                      </Col>
                    ))}
                </Row>

                <hr />
                <h5>Configurações</h5>
                <Row>
                  {campos.configuracoes.map(({ name, label }) => (
                    <Col md={4} key={name}>
                      <Form.Check
                        type="switch"
                        id={`conf-${name}`}
                        label={label}
                        checked={permissoes[name] === "S"}
                        onChange={() => togglePermissao(name)}
                      />
                    </Col>
                  ))}
                </Row>

                <hr />
                <h5>Parametros Globais</h5>
                <Row>
                  {campos.parametrosGlobais.map(({ name, label }) => (
                    <Col md={4} key={name}>
                      <Form.Check
                        type="switch"
                        id={`param-${name}`}
                        label={label}
                        checked={permissoes[name] === "S"}
                        onChange={() => togglePermissao(name)}
                      />
                    </Col>
                  ))}
                </Row>
              </Form>
            </div>
          </Tab>

          <Tab eventKey="empresas" title="Empresas">
            <div style={{ minHeight: TAB_CONTENT_MIN_HEIGHT, display: "flex", flexDirection: "column" }}>
              <Row className="g-2 align-items-center mb-2">
                <Col md={6}>
                  <small className="text-muted">
                    Selecionadas: <strong>{totalSelecionadas}</strong> de {totalEmpresas}
                  </small>
                </Col>
              </Row>

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius, 6px)",
                  padding: 8,
                }}
              >
                {empresasLoading && (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
                    <Spinner animation="border" role="status" />
                  </div>
                )}

                {!empresasLoading && empresasError && (
                  <div className="text-danger">{empresasError}</div>
                )}

                {!empresasLoading && !empresasError && empresasDisponiveis.length === 0 && (
                  <div className="text-muted">Nenhuma empresa encontrada.</div>
                )}

                {!empresasLoading && !empresasError && empresasDisponiveis.length > 0 && (
                  <>
                    <Table hover responsive size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>Fantasia / Razão Social</th>
                          <th style={{ width: 160 }}>CNPJ</th>
                          <th style={{ width: 100 }}>Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empresasPagina.map((e) => {
                          const checked = empresasIds.includes(e.id);
                          return (
                            <tr
                              key={e.id}
                              onClick={() => toggleEmpresa(e.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={checked}
                                  onChange={() => toggleEmpresa(e.id)}
                                  onClick={(ev) => ev.stopPropagation()}
                                />
                              </td>
                              <td>{e.fantasia || e.razao_social}</td>
                              <td>{e.cnpj || "-"}</td>
                              <td>{e.situacao === "A" ? "Ativo" : "Inativo"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        {totalEmpresas > 0 && (
                          <span className="text-muted small">
                            Mostrando {startRecordEmpresas} a {endRecordEmpresas} de {totalEmpresas} registros
                          </span>
                        )}
                      </div>
                      {totalPagesEmpresas > 1 && (
                        <Pagination size="sm" className="mb-0">
                          <Pagination.First
                            onClick={() => handleChangePageEmpresas(1)}
                            disabled={pageEmpresas === 1}
                          />
                          <Pagination.Prev
                            onClick={() => handleChangePageEmpresas(pageEmpresas - 1)}
                            disabled={pageEmpresas === 1}
                          />
                          {[...Array(totalPagesEmpresas)].map((_, index) => {
                            const pageNum = index + 1;
                            if (
                              pageNum === 1 ||
                              pageNum === totalPagesEmpresas ||
                              (pageNum >= pageEmpresas - 1 && pageNum <= pageEmpresas + 1)
                            ) {
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === pageEmpresas}
                                  onClick={() => handleChangePageEmpresas(pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            }
                            if (pageNum === 2 && pageEmpresas > 3) {
                              return <Pagination.Ellipsis key="ellipsis-start" disabled />;
                            }
                            if (pageNum === totalPagesEmpresas - 1 && pageEmpresas < totalPagesEmpresas - 2) {
                              return <Pagination.Ellipsis key="ellipsis-end" disabled />;
                            }
                            return null;
                          })}
                          <Pagination.Next
                            onClick={() => handleChangePageEmpresas(pageEmpresas + 1)}
                            disabled={pageEmpresas === totalPagesEmpresas}
                          />
                          <Pagination.Last
                            onClick={() => handleChangePageEmpresas(totalPagesEmpresas)}
                            disabled={pageEmpresas === totalPagesEmpresas}
                          />
                        </Pagination>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Tab>

          <Tab eventKey="tipo-documentos" title="Tipo Documentos">
            <div style={{ minHeight: TAB_CONTENT_MIN_HEIGHT, display: "flex", flexDirection: "column" }}>
              <Row className="g-2 align-items-center mb-2">
                <Col md={6}>
                  <small className="text-muted">
                    Selecionados: <strong>{totalTiposDocSelecionados}</strong> de {totalTiposDoc}
                  </small>
                </Col>
                <Col md={6} className="text-end">
                  <Button variant="link" size="sm" className="p-0 text-decoration-none me-2" onClick={selecionarTodosTiposDoc}>
                    Selecionar todos
                  </Button>
                  <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={desmarcarTodosTiposDoc}>
                    Desmarcar todos
                  </Button>
                </Col>
              </Row>

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius, 6px)",
                  padding: 8,
                }}
              >
                {tiposDocumentoLoading && (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
                    <Spinner animation="border" role="status" />
                  </div>
                )}

                {!tiposDocumentoLoading && tiposDocumentoError && (
                  <div className="text-danger">{tiposDocumentoError}</div>
                )}

                {!tiposDocumentoLoading && !tiposDocumentoError && tiposDocumentoDisponiveis.length === 0 && (
                  <div className="text-muted">Nenhum tipo de documento encontrado.</div>
                )}

                {!tiposDocumentoLoading && !tiposDocumentoError && tiposDocumentoDisponiveis.length > 0 && (
                  <>
                    <Table hover responsive size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th
                            style={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSortTiposDoc("nome")}
                            title="Ordenar por Nome"
                          >
                            Nome {sortTiposDocColumn === "nome" ? (sortTiposDocOrder === "asc" ? " ↑" : " ↓") : ""}
                          </th>
                          <th
                            style={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSortTiposDoc("descricao")}
                            title="Ordenar por Descrição"
                          >
                            Descrição {sortTiposDocColumn === "descricao" ? (sortTiposDocOrder === "asc" ? " ↑" : " ↓") : ""}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiposDocPagina.map((td) => {
                          const checked = tiposDocumentoIds.includes(td.id);
                          return (
                            <tr
                              key={td.id}
                              onClick={() => toggleTipoDocumento(td.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={checked}
                                  onChange={() => toggleTipoDocumento(td.id)}
                                  onClick={(ev) => ev.stopPropagation()}
                                />
                              </td>
                              <td>{td.nome || "-"}</td>
                              <td>{td.descricao || "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        {totalTiposDoc > 0 && (
                          <span className="text-muted small">
                            Mostrando {startRecordTiposDoc} a {endRecordTiposDoc} de {totalTiposDoc} registros
                          </span>
                        )}
                      </div>
                      {totalPagesTiposDoc > 1 && (
                        <Pagination size="sm" className="mb-0">
                          <Pagination.First
                            onClick={() => handleChangePageTiposDoc(1)}
                            disabled={pageTiposDoc === 1}
                          />
                          <Pagination.Prev
                            onClick={() => handleChangePageTiposDoc(pageTiposDoc - 1)}
                            disabled={pageTiposDoc === 1}
                          />
                          {[...Array(totalPagesTiposDoc)].map((_, index) => {
                            const pageNum = index + 1;
                            if (
                              pageNum === 1 ||
                              pageNum === totalPagesTiposDoc ||
                              (pageNum >= pageTiposDoc - 1 && pageNum <= pageTiposDoc + 1)
                            ) {
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === pageTiposDoc}
                                  onClick={() => handleChangePageTiposDoc(pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            }
                            if (pageNum === 2 && pageTiposDoc > 3) {
                              return <Pagination.Ellipsis key="ellipsis-start" disabled />;
                            }
                            if (pageNum === totalPagesTiposDoc - 1 && pageTiposDoc < totalPagesTiposDoc - 2) {
                              return <Pagination.Ellipsis key="ellipsis-end" disabled />;
                            }
                            return null;
                          })}
                          <Pagination.Next
                            onClick={() => handleChangePageTiposDoc(pageTiposDoc + 1)}
                            disabled={pageTiposDoc === totalPagesTiposDoc}
                          />
                          <Pagination.Last
                            onClick={() => handleChangePageTiposDoc(totalPagesTiposDoc)}
                            disabled={pageTiposDoc === totalPagesTiposDoc}
                          />
                        </Pagination>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Cancelar onClick={handleOnHide} />
          <Salvar onClick={handleSave} />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default GruposUsuarioModal;
